import React, { useState } from 'react';
import { BookOpen, Sparkles, Wand2, Expand, FileText } from 'lucide-react';
import FormModal from './FormModal';

// All sidebar routes grouped by persona — admin selects which page this guide is for
const ROUTE_OPTIONS = [
  { group: 'COMPANY ADMIN', routes: [
    { path: '/company/dashboard', label: 'Company Dashboard' },
    { path: '/company/compliance-pages', label: 'Compliance Pages' },
    { path: '/company/frameworks', label: 'Frameworks & Standards' },
    { path: '/company/evidence', label: 'Evidence Library' },
    { path: '/company/certifications', label: 'Certifications' },
    { path: '/company/carbon-credits', label: 'Carbon Credits' },
  ]},
  { group: 'COMPLIANCE MANAGEMENT', routes: [
    { path: '/compliance/obligations', label: 'Obligation Register' },
    { path: '/compliance/tasks', label: 'Task Management' },
    { path: '/compliance/capa', label: 'Corrective Actions (CAPA)' },
    { path: '/compliance/evidence-expiry', label: 'Evidence Expiry' },
    { path: '/compliance/gap-assessments', label: 'Gap Assessments' },
    { path: '/compliance/control-tests', label: 'Control Testing' },
    { path: '/compliance/training', label: 'Training & Competence' },
  ]},
  { group: 'GOVERNANCE & RISK', routes: [
    { path: '/compliance/policies', label: 'Policy Library' },
    { path: '/compliance/policy-attestations', label: 'Policy Attestations' },
    { path: '/compliance/risk-register', label: 'Risk Register' },
    { path: '/compliance/controls', label: 'Controls Library' },
    { path: '/compliance/vendor-risk', label: 'Vendor Risk' },
    { path: '/compliance/vendor-contracts', label: 'Vendor Contracts' },
    { path: '/compliance/assets', label: 'Asset Inventory' },
  ]},
  { group: 'INCIDENT & DATA PROTECTION', routes: [
    { path: '/compliance/incidents', label: 'Incident Register' },
    { path: '/compliance/privacy-reviews', label: 'Privacy Reviews (DPIA)' },
    { path: '/compliance/data-retention', label: 'Data Retention' },
  ]},
  { group: 'UK REGULATORY', routes: [
    { path: '/compliance/onboarding', label: 'Compliance Scoping' },
    { path: '/compliance/statutory-filings', label: 'Statutory Filings' },
    { path: '/compliance/modern-slavery', label: 'Modern Slavery' },
  ]},
  { group: 'AUDIT & ASSURANCE', routes: [
    { path: '/compliance/internal-audits', label: 'Internal Audits' },
    { path: '/compliance/approvals', label: 'Approval Queue' },
    { path: '/compliance/management-reviews', label: 'Management Reviews' },
    { path: '/compliance/scope', label: 'Scope Definition' },
    { path: '/compliance/company-audit-logs', label: 'Audit Logs' },
  ]},
  { group: 'REPORTING', routes: [
    { path: '/compliance/monitoring', label: 'Monitoring Dashboard' },
    { path: '/compliance/management-report', label: 'Management Reports' },
    { path: '/compliance/timeline', label: 'Compliance Timeline' },
    { path: '/compliance/trust-portal', label: 'Trust Portal' },
  ]},
  { group: 'CARBON & EMISSIONS', routes: [
    { path: '/emissions/scopes', label: 'Scopes Dashboard' },
    { path: '/emissions/factors', label: 'Emission Factors' },
    { path: '/emissions/bulk-calculator', label: 'Bulk Calculator' },
    { path: '/emissions/targets', label: 'Reduction Targets' },
    { path: '/emissions/offsets', label: 'Offset Credits' },
    { path: '/emissions/reports', label: 'Emission Reports' },
  ]},
  { group: 'COMPLIANCE USER', routes: [
    { path: '/compliance/dashboard', label: 'Compliance Dashboard' },
    { path: '/compliance/tasks', label: 'Assigned Pages' },
    { path: '/compliance/pending', label: 'Pending Evidence' },
    { path: '/compliance/frameworks', label: 'Active Frameworks' },
  ]},
  { group: 'AUDITOR', routes: [
    { path: '/audit/dashboard', label: 'Auditor Dashboard' },
    { path: '/audit/frameworks', label: 'Framework Reviews' },
    { path: '/audit/evidence', label: 'Evidence Review' },
    { path: '/audit/non-conformities', label: 'Non-Conformities' },
    { path: '/audit/certifications', label: 'Certifications' },
    { path: '/audit/summary', label: 'Assessment Summary' },
  ]},
  { group: 'SYSTEM ADMIN', routes: [
    { path: '/admin/dashboard', label: 'Admin Dashboard' },
    { path: '/admin/system-settings', label: 'System Settings' },
    { path: '/admin/companies', label: 'Companies' },
    { path: '/admin/users', label: 'Users' },
    { path: '/compliance/definition/library', label: 'Framework Library' },
    { path: '/compliance/definition/frameworks', label: 'Frameworks' },
    { path: '/compliance/definition/builder', label: 'Template Builder' },
    { path: '/compliance/definition/templates', label: 'Templates' },
    { path: '/compliance/definition/mapping', label: 'Framework Mapping' },
    { path: '/compliance/definition/assignments', label: 'Assignments' },
    { path: '/compliance/definition/guidance', label: 'Guidance & Process Maps' },
    { path: '/compliance/definition/parser', label: 'Document Parser' },
  ]},
];

interface Guidance {
  id: number;
  title: string;
  description: string;
  content: string;
  category: string;
  framework_id?: number;
  scope_id?: number;
  section_id?: number;
  page_id?: number;
  route_path?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface GuidanceModalProps {
  onClose: () => void;
  onSubmit: (guidance: Partial<Guidance>) => Promise<void> | void;
  selectedGuidance: Guidance | null;
}

export const GuidanceModal: React.FC<GuidanceModalProps> = ({
  onClose,
  onSubmit,
  selectedGuidance,
}) => {
  const [guidance, setGuidance] = useState<Partial<Guidance>>(
    selectedGuidance || {
      title: '',
      description: '',
      content: '',
      category: '',
      is_active: true,
    }
  );

  const [loading, _setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const handleAiAction = async (action: 'improve' | 'expand' | 'generate') => {
    if (!guidance.content && action !== 'generate') return;
    setAiLoading(true);
    try {
      const token = localStorage.getItem('token');
      const csrf = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

      // Find the selected page info for context
      const selectedRoute = ROUTE_OPTIONS.flatMap(g => g.routes).find(r => r.path === (guidance as any).route_path);
      const selectedGroup = ROUTE_OPTIONS.find(g => g.routes.some(r => r.path === (guidance as any).route_path));
      const pageName = selectedRoute?.label || guidance.title || 'Untitled';
      const persona = selectedGroup?.group || 'user';

      const text = action === 'generate'
        ? `You are writing a system guide for the "${pageName}" page in an enterprise compliance management platform (EIS-Tech). This page is used by the ${persona} persona.

The page is located at route: ${(guidance as any).route_path || '/unknown'}

Write a comprehensive, accurate guide that covers:
## What This Page Does
(Explain the purpose of this specific page in the compliance workflow)

## How To Use It
(Step-by-step instructions for common tasks on this page)

## Key Terminology
(Define any compliance/industry terms used on this page)

## Best Practices
(Tips for getting the most value from this page)

## Regulatory Context
(If applicable, which regulations or standards this page helps with — ISO 27001, UK GDPR, Companies Act, etc.)

Write in clear, professional English. Be specific to this page — do not give generic compliance advice. Use ## headings for each section.`
        : guidance.content || '';

      const res = await fetch('/api/pages/admin/ai-convert', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        body: JSON.stringify({ text, action: action === 'generate' ? 'expand' : action === 'expand' ? 'expand' : 'improve' }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.markdown) setGuidance(prev => ({ ...prev, content: data.markdown }));
      }
    } catch { /* silent */ }
    setAiLoading(false);
  };

  const handleSubmit = async () => {
    try {
      await onSubmit(guidance);
      onClose();
    } catch {
      // error already handled by parent; keep modal open
    }
  };

  const isCreate = !selectedGuidance?.id;

  return (
    <FormModal
      title={isCreate ? 'Create Guidance' : 'Edit Guidance'}
      subtitle={isCreate ? 'Create new guidance content' : 'Modify existing guidance'}
      icon={<BookOpen className="text-white" size={24} />}
      onSubmit={handleSubmit}
      onCancel={onClose}
      submitText={isCreate ? 'Create Guidance' : 'Update Guidance'}
      cancelText="Cancel"
      loading={loading}
      size="large"
    >
      <div className="space-y-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">Title *</label>
          <input
            type="text"
            value={guidance.title || ''}
            onChange={(e) => setGuidance({ ...guidance, title: e.target.value })}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            placeholder="Enter guidance title"
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">Description</label>
          <textarea
            value={guidance.description || ''}
            onChange={(e) => setGuidance({ ...guidance, description: e.target.value })}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            rows={3}
            placeholder="Brief description of the guidance"
            disabled={loading}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Category</label>
            <select
              value={guidance.category || ''}
              onChange={(e) => setGuidance({ ...guidance, category: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
              disabled={loading}
            >
              <option value="">Select category</option>
              <option value="compliance">Compliance</option>
              <option value="process">Process</option>
              <option value="documentation">Documentation</option>
              <option value="training">Training</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Active</label>
            <div className="flex items-center mt-3">
              <input
                type="checkbox"
                checked={guidance.is_active || false}
                onChange={(e) => setGuidance({ ...guidance, is_active: e.target.checked })}
                className="h-5 w-5 text-slate-600 rounded focus:ring-2 focus:ring-slate-600 focus:ring-offset-0"
                disabled={loading}
              />
              <span className="ml-3 text-sm text-slate-700">Make this guidance active</span>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">Linked Page (System Guide)</label>
          <select
            value={(guidance as any).route_path || ''}
            onChange={(e) => {
              const selected = e.target.value;
              const label = ROUTE_OPTIONS.flatMap(g => g.routes).find(r => r.path === selected)?.label || '';
              setGuidance({ ...guidance, route_path: selected, title: guidance.title || label } as any);
            }}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            disabled={loading}
          >
            <option value="">-- Select a page (optional) --</option>
            {ROUTE_OPTIONS.map(group => (
              <optgroup key={group.group} label={group.group}>
                {group.routes.map(route => (
                  <option key={route.path} value={route.path}>{route.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <p className="text-xs text-slate-500">Link this guide to a sidebar page. Users see it when they click the help icon on that page.</p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-slate-700">Content *</label>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => handleAiAction('generate')}
                disabled={aiLoading || !guidance.title}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 disabled:opacity-50 transition-colors"
              >
                <Sparkles size={12} /> {aiLoading ? 'Generating...' : 'AI Generate'}
              </button>
              <button
                type="button"
                onClick={() => handleAiAction('improve')}
                disabled={aiLoading || !guidance.content}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50 transition-colors"
              >
                <Wand2 size={12} /> Improve
              </button>
              <button
                type="button"
                onClick={() => handleAiAction('expand')}
                disabled={aiLoading || !guidance.content}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 disabled:opacity-50 transition-colors"
              >
                <Expand size={12} /> Expand
              </button>
            </div>
          </div>
          <textarea
            value={guidance.content || ''}
            onChange={(e) => setGuidance({ ...guidance, content: e.target.value })}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all font-mono text-sm"
            rows={12}
            placeholder="Enter the guidance content using Markdown (## headings for sections)"
            disabled={loading}
          />
          <p className="text-xs text-slate-500">Use Markdown format with ## headings. AI buttons can generate, improve, or expand content.</p>
        </div>
      </div>
    </FormModal>
  );
};